// Shared types, stock-image catalogues, and URL helpers used across the
// olive-newsletter-builder family of section components and HTML generators.

// Header image options - using internal stock images (professional photos)
export const headerImageOptions = [
  // Business & Office
  { id: 1, url: '/stock-images/conference-room.jpg', name: 'Conference Room' },
  { id: 2, url: '/stock-images/team-discussion.jpg', name: 'Team Discussion' },
  { id: 3, url: '/stock-images/modern-office.jpg', name: 'Modern Office' },
  { id: 4, url: '/stock-images/business-meeting.jpg', name: 'Business Meeting' },
  { id: 5, url: '/stock-images/data-analytics.jpg', name: 'Data Analytics' },
  { id: 6, url: '/stock-images/city-buildings.jpg', name: 'City Buildings' },
  { id: 7, url: '/stock-images/modern-workspace.jpg', name: 'Modern Workspace' },
  { id: 8, url: '/stock-images/business-collaboration.jpg', name: 'Business Collaboration' },
  { id: 9, url: '/stock-images/product-launch.jpg', name: 'Product Launch' },
  { id: 10, url: '/stock-images/digital-presentation.jpg', name: 'Digital Presentation' },
  // Aviation & Airports
  { id: 11, url: '/stock-images/airport-terminal.jpg', name: 'Airport Terminal' },
  { id: 12, url: '/stock-images/air-traffic-control.jpg', name: 'Air Traffic Control' },
  { id: 13, url: '/stock-images/airport-runway.jpg', name: 'Airport Runway' },
  // Police & Law Enforcement
  { id: 14, url: '/stock-images/police-vehicles.jpg', name: 'Police Vehicles' },
  { id: 15, url: '/stock-images/police-patrol.jpg', name: 'Police Patrol' },
  // Fire Department & Emergency
  { id: 16, url: '/stock-images/fire-department.jpg', name: 'Fire Department' },
  { id: 17, url: '/stock-images/firefighters-response.jpg', name: 'Firefighters Response' },
  { id: 18, url: '/stock-images/firefighters-action.jpg', name: 'Firefighters in Action' },
  { id: 19, url: '/stock-images/ambulance-emergency.jpg', name: 'Ambulance Emergency' },
  { id: 20, url: '/stock-images/emergency-vehicles.jpg', name: 'Emergency Vehicles' },
  // Healthcare & Hospital
  { id: 21, url: '/stock-images/hospital-corridor.jpg', name: 'Hospital Corridor' },
  { id: 22, url: '/stock-images/hospital-surgery.jpg', name: 'Hospital Surgery' },
  { id: 23, url: '/stock-images/trauma-center.jpg', name: 'Trauma Center' },
  // Military & Defense
  { id: 24, url: '/stock-images/military-formation.jpg', name: 'Military Formation' },
  { id: 25, url: '/stock-images/military-helicopter.jpg', name: 'Military Helicopter' },
  { id: 26, url: '/stock-images/military-parade.jpg', name: 'Military Parade' },
  // Technology & Infrastructure
  { id: 27, url: '/stock-images/server-room.jpg', name: 'Server Room' },
  { id: 28, url: '/stock-images/control-room.jpg', name: 'Control Room' },
  { id: 29, url: '/stock-images/network-infrastructure.jpg', name: 'Network Infrastructure' },
  // Communications
  { id: 30, url: '/stock-images/radio-tower.jpg', name: 'Radio Tower' },
  { id: 31, url: '/stock-images/communication-tower.jpg', name: 'Communication Tower' },
  { id: 32, url: '/stock-images/satellite-dish.jpg', name: 'Satellite Communications' },
  // Education
  { id: 33, url: '/stock-images/school-building.jpg', name: 'School Building' },
  { id: 34, url: '/stock-images/school-campus.jpg', name: 'School Campus' },
  // Maritime & Ports
  { id: 35, url: '/stock-images/port-harbor.jpg', name: 'Port Harbor' },
  { id: 36, url: '/stock-images/shipping-containers.jpg', name: 'Shipping Containers' },
  { id: 37, url: '/stock-images/naval-vessels.jpg', name: 'Naval Vessels' },
  { id: 38, url: '/stock-images/coast-guard-ship.jpg', name: 'Coast Guard Ship' },
  // Energy & Utilities
  { id: 39, url: '/stock-images/solar-energy.jpg', name: 'Solar Energy' },
  { id: 40, url: '/stock-images/wind-energy.jpg', name: 'Wind Energy' },
  // Security & Surveillance
  { id: 41, url: '/stock-images/security-camera.jpg', name: 'Security Camera' },
  { id: 42, url: '/stock-images/surveillance-system.jpg', name: 'Surveillance System' },
  // Transportation
  { id: 43, url: '/stock-images/highway-infrastructure.jpg', name: 'Highway Infrastructure' },
]

// Default news images - using internal stock images
export const defaultNewsImages = [
  '/stock-images/data-analytics.jpg', // Analytics Dashboard
  '/stock-images/team-discussion.jpg', // Business Team
  '/stock-images/business-meeting.jpg', // Office Meeting
]

// Helper to convert relative URLs to absolute for emails AND previews. Email
// clients dereference relative paths against their own origin, and the SPA
// preview renders on a different origin than crm-api / the public site — so we
// anchor every asset URL to the public host. Single source of truth lives in
// `crm/lib/public-url.ts`; re-exported here for the existing import sites.
export { toAbsoluteUrl, toPreviewImageUrl } from "@crm-src/lib/public-url"

// Shared types

// A single block in the visual newsletter editor. `content` shape varies
// per `type` and is treated as `any` here intentionally — each section
// component validates its own content keys at the call site.
export interface NewsletterSection {
  id: string
  type: 'header' | 'introducing' | 'case_study' | 'company_news' | 'footer'
  content: any
  backgroundColor?: string
}

// Subset of tenant info fields the newsletter renders (header logo/name,
// footer contact block). Optional everywhere — partial info renders gracefully.
export interface TenantInfo {
  name: string
  logo_url?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
}
